import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { INIT_CHART_OPTION_SMALL, KPI_CHARTS_WITH_INCREASED_BOTTOM_GRID } from '@catalogues';
import {
  INIT_KPI_CHART_OPTION_LARGE,
  INIT_KPI_CHART_OPTION_MEDIUM,
  INIT_KPI_CHART_OPTION_SMALL
} from '@catalogues';
import { ChartSize, KpiChartId } from '@models';
import { CommonFunctionsService, StudyFilterService } from '@services';
import { EChartsOption } from 'echarts';
import { ECharts } from 'echarts/core';

@Component({
  selector: 'grouped-bar-chart',
  templateUrl: './grouped-bar-chart.component.html',
  styleUrl: './grouped-bar-chart.component.scss'
})
export class GroupedBarChartComponent implements OnChanges {
  chartInstance!: ECharts;
  @Input() filterService: any = inject(StudyFilterService); // Default to StudyFilterService
  @Input() initOption:
    | typeof INIT_CHART_OPTION_SMALL
    | typeof INIT_KPI_CHART_OPTION_SMALL
    | typeof INIT_KPI_CHART_OPTION_MEDIUM
    | typeof INIT_KPI_CHART_OPTION_LARGE
    | any;
  @Input() sortedData!: string[];
  @Input() chartOption!: EChartsOption | any;
  @Input() showAllKeys = true;
  @Input() field = '';
  @Input() kpiXAxisLabel: string | undefined = '';
  @Input() kpiAxisKeysShownDefault: number | undefined = undefined;
  @Input() kpiChartSize: ChartSize = 'medium';
  @Input() kpiOnTrack?: number;
  @Input() kpiOnTarget?: number;
  @Input() isPercent = false;
  @Input() kpiChartIdentifier!: KpiChartId;
  @Input() includeToggleInHeightCalculation = false;
  @Output() expandRequested = new EventEmitter<void>();

  public chartKeys: string[] = [];
  public Math = Math;
  render = true;
  private readonly MEDIUM_CHART_DENSE_ITEM_THRESHOLD = 10;
  private readonly MEDIUM_CHART_ITEM_HEIGHT = 30;
  KPI_CHARTS_WITH_NO_TARGET_LINE: string[] = [];
  KPI_CHARTS_WITH_NO_ON_TRACK_LINE: string[] = [];

  private cdr = inject(ChangeDetectorRef);
  private commonFunctionsService = inject(CommonFunctionsService);

  onChartInit(chart: ECharts) {
    this.chartInstance = chart;
    this.commonFunctionsService.resizeChartWhenContainerIsReady(chart, {
      shouldStop: () => chart.isDisposed?.()
    });
  }

  onExpandButtonClick() {
    this.expandRequested.emit();
  }

  onChartClick(field: string, event: any) {
    if (event && event.name && field !== '') {
      this.filterService.toggleFilter(field, event.name, 'chart');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changesArg: any = changes;
    if (changesArg && changesArg['initOption'] && this.chartInstance) {
      this.chartInstance.dispose();
      this.render = false;
      this.cdr.detectChanges();
      this.render = true;
    }

    this.updateChartOptions();
  }

  // Keep an eye on this method to try to reduce complexity
  private updateChartOptions() {
    if (this.chartOption && this.sortedData) {
      let keys = Object.keys(this.sortedData).filter((k) => k !== '');
      keys = this.getSlicedKeys(keys);
      this.chartKeys = keys;

      const seriesArr = Array.isArray(this.chartOption.series)
        ? this.chartOption.series
        : [this.chartOption.series];

      const grid = this.chartOption.grid ? { ...this.chartOption.grid } : {};
      this.setKpiChartGrid(grid);
      const xAxis = this.chartOption.xAxis ? { ...this.chartOption.xAxis } : {};
      this.setKpiChartAxisStyle(xAxis);
      const yAxis = this.chartOption.yAxis ? { ...this.chartOption.yAxis } : {};
      this.setKpiChartAxisStyle(yAxis);
      const showLabels = this.canShowKpiLabels();

      let firstGroupedSeriesConfig = this.buildGroupedSeriesConfig(
        keys,
        showLabels,
        seriesArr[0] ?? {},
        true
      );

      const secondGroupedSeriesConfig = this.buildGroupedSeriesConfig(
        keys,
        showLabels,
        seriesArr[1] ?? {},
        false
      );

      this.setYAxisTruncation(yAxis);
      this.setXAxisMax(xAxis);
      this.setChartHeight(grid);
      this.setKpiAxisName(xAxis);

      this.setDataKeysForAxis(xAxis, keys);
      this.setDataKeysForAxis(yAxis, keys);

      firstGroupedSeriesConfig = this.buildOnTrackLine(firstGroupedSeriesConfig);

      this.chartOption = {
        ...this.chartOption,
        grid,
        xAxis,
        yAxis,
        series: [firstGroupedSeriesConfig, secondGroupedSeriesConfig]
      };
    }
  }

  private buildGroupedSeriesConfig(
    keys: string[],
    showLabels: boolean,
    seriesBase: any,
    isFirstGroupedSeries: boolean
  ) {
    return {
      label: {
        show: showLabels,
        position: 'right',
        formatter: (params: any) => {
          const data = this.sortedData[params.name];
          if (
            data &&
            typeof data === 'object' &&
            (isFirstGroupedSeries ? 'numerator' in data : 'denominator' in data)
          ) {
            return this.formatLabel(data, isFirstGroupedSeries);
          } else {
            return '';
          }
        }
      },
      ...seriesBase,
      data: this.getDataValues(keys, isFirstGroupedSeries)
    };
  }

  private validateAxisHasCategory(axis: any): boolean {
    if (typeof axis === 'object' && axis && 'type' in axis && (axis as any).type === 'category') {
      return true;
    }
    return false;
  }

  private setDataKeysForAxis(axis: any, keys: string[]) {
    if (this.validateAxisHasCategory(axis)) {
      (axis as any).data = keys;
    }
  }

  private formatLabel(data: any, isFirstGroupedSeries: boolean) {
    if (isFirstGroupedSeries) {
      return data.numerator === 0 ? '--' : `${data.numerator}`;
    } else {
      return data.denominator === 0 ? '--' : `${data.denominator}`;
    }
  }

  getSortedDataKeysLength(): number {
    return this.sortedData ? Object.keys(this.sortedData).length : 0;
  }

  private setKpiChartGrid(grid: any) {
    if (this.kpiChartSize === 'small' || this.kpiChartSize === 'medium') {
      grid.top = '5%';
      grid.bottom = '10%';
    }
  }

  private setKpiChartAxisStyle(axis: any) {
    let fontSize = 12;
    let fontWeight = 'normal';
    let nameFontWeight = 'normal';

    if (this.kpiChartSize === 'large') {
      fontSize = 12;
      fontWeight = 'normal';
      nameFontWeight = 'bold';
    } else if (this.kpiChartSize === 'medium') {
      nameFontWeight = 'bold';
    }

    if (this.kpiChartSize === 'medium' || this.kpiChartSize === 'large') {
      axis.axisLabel = {
        ...axis.axisLabel,
        color: '#1B1B1D',
        fontSize: fontSize,
        fontWeight: fontWeight,
        fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
      };
      axis.nameTextStyle = {
        ...axis.nameTextStyle,
        color: '#1B1B1D',
        fontSize: fontSize,
        fontWeight: nameFontWeight,
        fontFamily: 'HelveticaNeue, Helvetica, Arial, sans-serif'
      };
    }
  }

  private setYAxisTruncation(yAxis: any) {
    switch (this.kpiChartSize) {
      case 'small':
        yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 100 };
        break;
      case 'medium':
        yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 250 };
        break;
      case 'large':
        yAxis.axisLabel = { ...yAxis.axisLabel, overflow: 'truncate', margin: 10, width: 650 };
        break;
    }
  }

  private setXAxisMax(xAxis: any) {
    if (this.isPercent) {
      if (this.chartKeys.length > 0) {
        const valuesArray = this.chartKeys.map((k) => (this.sortedData as any)[k]);
        const maxNumeratorValue = Math.max(
          ...valuesArray.map((d: any) => (d.numerator ? d.numerator : 0))
        );
        const maxDenominatorValue = Math.max(
          ...valuesArray.map((d: any) => (d.denominator ? d.denominator : 0))
        );
        const maxValue = Math.max(maxNumeratorValue, maxDenominatorValue);
        if (maxValue <= 100) {
          xAxis.max = 100;
        } else if (maxValue > 100) {
          xAxis.max = maxValue + 20;
        }
      } else {
        xAxis.max = 100;
      }
    }
  }

  private canShowKpiLabels() {
    const allowedKpiCharts = ['groupedBarEnrollmentByCountry'];
    return allowedKpiCharts.includes(this.kpiChartIdentifier) && this.kpiChartSize !== 'small';
  }

  private buildOnTrackLine(seriesConfig: any) {
    const markLineData = [];
    if (
      this.kpiOnTrack !== undefined &&
      !this.KPI_CHARTS_WITH_NO_ON_TRACK_LINE.includes(this.kpiChartIdentifier)
    ) {
      const onTrackLabel =
        this.kpiChartSize === 'small' ? 'On track' : 'On track: ' + this.kpiOnTrack + '%';

      markLineData.push({
        xAxis: this.kpiOnTrack,
        lineStyle: { color: '#F35200', type: 'dashed' },
        label: { show: true, formatter: onTrackLabel, position: 'end', offset: [0, 15] }
      });
    }
    if (
      this.kpiOnTarget !== undefined &&
      !this.KPI_CHARTS_WITH_NO_TARGET_LINE.includes(this.kpiChartIdentifier)
    ) {
      const onTargetLabel =
        this.kpiChartSize === 'small' ? 'Target' : 'Target: ' + this.kpiOnTarget + '%';
      markLineData.push({
        xAxis: this.kpiOnTarget,
        lineStyle: { color: '#F35200', type: 'solid' },
        label: { show: true, formatter: onTargetLabel, position: 'end', offset: [0, 30] }
      });
    }
    if (markLineData.length) {
      seriesConfig = {
        ...seriesConfig,
        markLine: {
          symbol: 'none',
          data: markLineData
        }
      };
    }
    return seriesConfig;
  }

  private setKpiAxisName(xAxis: any) {
    if (
      this.kpiChartSize !== 'small' &&
      typeof xAxis === 'object' &&
      xAxis &&
      'name' in xAxis &&
      this.kpiXAxisLabel !== undefined
    ) {
      // set the xAxis name
      (xAxis as any).name = this.kpiXAxisLabel;
    } else if (this.kpiChartSize === 'small') {
      (xAxis as any).name = '';
    }
  }

  private getSlicedKeys(keys: string[]) {
    if (!this.showAllKeys) {
      if (this.kpiAxisKeysShownDefault !== undefined) {
        keys = keys.slice(-this.kpiAxisKeysShownDefault);
      } else {
        keys = keys.slice(-4);
      }
    }
    return keys;
  }

  private getDataValues(keys: string[], isFirstGroupedSeries: boolean) {
    return keys.map((k: any) =>
      isFirstGroupedSeries
        ? (this.sortedData as any)[k].numerator
        : (this.sortedData as any)[k].denominator
    );
  }

  private setChartHeight(grid: any) {
    if (
      this.showAllKeys &&
      this.kpiChartSize === 'medium' &&
      Object.keys(this.sortedData).length >= 3
    ) {
      this.initOption = { ...INIT_KPI_CHART_OPTION_MEDIUM };
      const chartSizeWithKeysLength = this.chartKeys.length * this.MEDIUM_CHART_ITEM_HEIGHT;
      const chartHeightWithToggle = 250;
      const chartHeightWithoutToggle = 270;
      const minHeight = this.includeToggleInHeightCalculation
        ? chartHeightWithToggle
        : chartHeightWithoutToggle;
      const isDenseDataset = this.chartKeys.length >= this.MEDIUM_CHART_DENSE_ITEM_THRESHOLD;
      const increaseGridBottom = chartSizeWithKeysLength < minHeight;

      if (isDenseDataset) {
        // Dense grouped charts need internal height larger than container for vertical scroll.
        this.initOption.height = Math.max(minHeight, chartSizeWithKeysLength);
      } else {
        // Keep medium grouped charts aligned to parent container height.
        delete this.initOption.height;
      }
      grid.top = '1%';
      if (KPI_CHARTS_WITH_INCREASED_BOTTOM_GRID.includes(this.kpiChartIdentifier)) {
        if (increaseGridBottom) {
          grid.bottom = '16%';
        } else {
          grid.bottom = '4%';
        }
      } else {
        grid.bottom = isDenseDataset ? '2%' : '12%';
      }
    }
  }

  getChartHeight(): string | number | null {
    if (this.kpiChartSize === 'small') {
      return '125px';
    }

    if (this.kpiChartSize === 'medium') {
      return null;
    }

    if (this.kpiChartSize === 'large' && this.chartKeys.length > 30) {
      return `${Math.max(500, this.chartKeys.length * 30)}px`;
    }
    return null;
  }
}
